<template>
    <div class="card flex-grow-1">
        <div v-show="panel.sub_header !== '' && panel.sub_header != '<p></p>'" class="card-header">
            <div
                class="rich-text-content small mt-1 mb-0"
                v-html="panel.sub_header"
            ></div>
        </div>
        <div class="card-body panel-card-body d-flex flex-column px-0 py-0">
            <div
                v-if="series"
                class="card-body text-left vitals-pane"
                :class="{
                    'vitals-pane-no-header': !Boolean(panel.header),
                    'vitals-pane-header': Boolean(panel.header),
                }"
            >
                <div v-for="(vitalsSeries, $index) in series" :key="$index" class="row vitals-row">
                    <div class="col-12" :style="{ color: vitalsSeries.hex_color }">
                        <!-- Vital Sign Label Display -->
                        <div class="h4 vitals-label float-left mt-1">
                            <strong v-html="vitalsSeries.code"></strong>
                        </div>

                        <!-- Vital Sign Data Display -->
                        <div class="h4 vitals-value float-right">
                            <div v-if="useUploadedData">
                                {{ vitalsSeries.points[displayIndex] }}
                            </div>
                            <div v-else>
                                <span v-if="vitalsSeries.currentNumerator !== undefined">
                                    {{ displayVitalsValue(vitalsSeries, vitalsSeries.currentNumerator) }}</span
                                ><span v-if="vitalsSeries.currentDenominator !== undefined"
                                >/{{ displayVitalsValue(vitalsSeries, vitalsSeries.currentDenominator) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "VitalsPanel",
    props: {
        panel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            displayIndex: 0,
            timeoutId: null
        }
    },
    computed: {
        series() {
            return this.panel.vitals.series
        },
        useUploadedData() {
            return this.panel.vitals.use_uploaded_data
        },
    },
    methods: {
        // Move to the next set of data points.
        updateVitals(timeBase) {
            if (this.panel.vitals.use_uploaded_data) {
                // Stop/freeze at the last data point - assumes all data series are the same length.
                if (this.displayIndex >= this.series[0].points.length - 1) {
                    return
                }
                this.displayIndex++
            } else {
                let vm = this
                this.series.forEach((series) => {

                    // Get series boundaries, based on config and active triggers.
                    let boundaries = vm.getSeriesBoundaries(series)

                    if (series.currentNumerator !== undefined) {
                        // Update the numerator.
                        let newNumerator = updateVitalsValue(
                            series.currentNumerator,
                            series,
                            boundaries.lowNumerator,
                            boundaries.highNumerator,
                            'numeratorDirection'
                        )
                        series.currentNumerator = newNumerator
                    }

                    if (series.currentDenominator !== undefined) {
                        // Update the denominator.
                        let newDenominator = updateVitalsValue(
                            series.currentDenominator,
                            series,
                            boundaries.lowDenominator,
                            boundaries.highDenominator,
                            'denominatorDirection'
                        )
                        series.currentDenominator = newDenominator
                    }
                })
            }
            this.timeoutVitals(timeBase)
        },
        // Retrieve the boundaries for a vitals series, based on its config and the currently
        // applied triggers.
        getSeriesBoundaries(series) {

            let triggerStack = series.triggerStack || []

            let lowNumerator = series.low_boundary_num
            let lowDenominator = series.low_boundary_denom
            let highNumerator = series.high_boundary_num
            let highDenominator = series.high_boundary_denom

            // Find the most recent 'replace' type trigger that's been applied, if any.
            let replaceTypeTriggers = triggerStack.filter(t => t.change_type == 'replace_with')
            if (replaceTypeTriggers.length > 0) {
                let recentTrigger = replaceTypeTriggers[0]
                lowNumerator = recentTrigger.low_boundary_num
                lowDenominator = recentTrigger.low_boundary_denom
                highNumerator = recentTrigger.high_boundary_num
                highDenominator = recentTrigger.high_boundary_denom
            }

            // Find all 'increase' type triggers.
            let increaseTypeTriggers = triggerStack.filter(t => t.change_type == 'increase_by')
            let totalIncreaseNum = 0
            let totalIncreaseDenom = 0
            if (increaseTypeTriggers.length > 0) {
                totalIncreaseNum = increaseTypeTriggers.reduce((total, trigger) => {
                    return total + trigger.increase_value_num
                }, 0)
                totalIncreaseDenom = increaseTypeTriggers.reduce((total, trigger) => {
                    return total + trigger.increase_value_denom
                }, 0)
            }

            // Add in the increases from triggers.
            lowNumerator += totalIncreaseNum
            lowDenominator += totalIncreaseDenom
            highNumerator += totalIncreaseNum
            highDenominator += totalIncreaseDenom

            return {
                lowNumerator: lowNumerator,
                lowDenominator: lowDenominator,
                highNumerator: highNumerator,
                highDenominator: highDenominator
            }
        },
        // Update the vitals after a timeout.
        timeoutVitals(timeBase) {
            let vm = this
            this.timeoutId = window.setTimeout(function () {
                vm.updateVitals(timeBase)
            }, timeBase)
        },
        displayVitalsValue(vitalsSeries, value) {
            if (vitalsSeries.show_decimals) {
                return value.toFixed(1)
            }
            return Math.round(value)
        },
        // Activate or deactivate vitals triggers.
        activateTriggers(payload) {
            let triggers = payload.triggers
            let activate = payload.activate

            let vm = this
            triggers.forEach(function(trigger) {
                // Find the series that the trigger applies to.
                let series = vm.panel.vitals.series.find(s => s.id == trigger.vitals_series_id)

                if (activate) {
                    // Activating a trigger, so add it the series trigger stack.
                    if ('triggerStack' in series) {
                        let existingTrigger = series.triggerStack.find(t => t.id == trigger.id)
                        if (!existingTrigger) {
                            series.triggerStack.unshift(trigger)
                        }
                    } else {
                        series.triggerStack = [trigger]
                    }
                } else {
                    // The trigger is being deactivated, so remove it from the trigger stack.
                    let index = series.triggerStack.map(t => t.id).indexOf(trigger.id)
                    if (index != -1) {
                        series.triggerStack.splice(index, 1)
                    }
                }
            })
        },
    },
    mounted() {
        let vm = this
        if (!this.useUploadedData) {
            // Initialize the value and direction of each vitals sign.
            this.series.forEach((series) => {
                
                let range = 0

                if (series.low_boundary_num !== null) {
                    // Set up the initial numerator value. First calculate the full range
                    // between the boundaries.
                    range = series.high_boundary_num - series.low_boundary_num
                    // Set the initial value to a random point between the boundaries.
                    let initialNumerator = Math.round(series.low_boundary_num + range * Math.random())
                    this.$set(series, "currentNumerator", initialNumerator)

                    // Randomly set the trajectory of the vitals sign.
                    if (Math.random() > 0.5) {
                        series.numeratorDirection = "down"
                    } else {
                        series.numeratorDirection = "up"
                    }
                }

                if (series.low_boundary_denom !== null) {
                    // Calculate the full range between the denominator boundaries.
                    range = series.high_boundary_denom - series.low_boundary_denom
                    // Set the initial value to a random point between the boundaries.
                    let initialDenominator = Math.round(series.low_boundary_denom + range * Math.random())
                    this.$set(series, "currentDenominator", initialDenominator)

                    // Randomly set the trajectory of the vitals sign.
                    if (Math.random() > 0.5) {
                        series.denominatorDirection = "down"
                    } else {
                        series.denominatorDirection = "up"
                    }
                }

                // Initiate time triggers.
                if (series.time_triggers.length > 0) {
                    let vm = this
                    // Initiate a timeout for each trigger, using the configured delay.
                    series.time_triggers.forEach((trigger) => {
                        let timeoutId = window.setTimeout(function () {
                            vm.activateTriggers({
                                triggers: [trigger],
                                activate: true
                            })
                        }, trigger.time_delay_seconds * 1000)
                    })
                }
            })
        }
        let timeBasis = this.useUploadedData ? 800 : 1700

        vm.timeoutVitals(timeBasis)
    },
    destroyed() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
    },
}

// Return a new vitals value based on the current value and series.
function updateVitalsValue(currentValue, series, lowBoundary, highBoundary, directionKey) {
    // Determine the 'step' for the vitals sign with some randomness
    // (i.e. how much to move by).
    let step = Math.round(10 * (1 + Math.random())) / 10

    // Determine the new value based on the current trajectory of the vitals sign.
    let newValue = null
    if (series[directionKey] == "up") {
        newValue = currentValue + step
    } else {
        newValue = currentValue - step
    }

    // Some of the time, just freeze at the same value.
    if (Math.random() > 0.5) {
        newValue = currentValue
    }

    // Define a maximum step that a vital sign can change by in one update.
    // This is useful when triggers change values dramatically, to add realism.
    const stepLimit = Math.round(2 + 2 * Math.random(), 2)

    // Make sure the new value isn't outside the configured boundaries for
    // this vitals sign.
    if (newValue >= highBoundary) {
        series[directionKey] = "down"
        if ((newValue - highBoundary) > stepLimit) {
            newValue = currentValue - stepLimit
        } else {
            newValue = highBoundary
        }
    } else if (newValue <= lowBoundary) {
        series[directionKey] = "up"
        if ((lowBoundary - newValue) > stepLimit) {
            newValue = currentValue + stepLimit
        } else {
            newValue = lowBoundary
        }
    }

    // Replace nulls with zeros.
    newValue = newValue == null ? 0 : newValue
    return newValue
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.vitals-value {
    font-size: 2.6rem;
    font-weight: 300;
}

.vitals-label {
    font-size: 1.5rem;
    font-weight: 400;
}

.vitals-pane {
    background-color: #2c3135;
}

.vitals-pane-no-header {
    border-radius: $border-radius-lg;
}

.vitals-pane-header {
    border-radius: 0px 0px $border-radius-lg $border-radius-lg;
}

.text-red {
    color: #e43949;
}
.text-blue {
    color: #3878e0;
}

.vitals-row {
    margin-bottom: 3px;
}
</style>